import * as React from 'react';

import { pipe } from 'fp-ts/lib/function';
import * as E from 'fp-ts/Either';
import {
  FullMatchFragment,
  useCloseMatchVotingMutation,
} from '../../generated/graphql';
import { InternalLink } from '../../components/InternalLink';
import { SubmitButton } from '../../components/SubmitButton';
import { paths } from '../../paths';
import { ButtonLink } from '../../components/Button';

import { buildMutationMessage } from '../../utils/buildMutationMessage';
import { useAlert } from '../../contexts/AlertContext';
import { LoadingSpinner } from '../../components/LoadingContainer';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useAlreadyCastVotes } from './useAlreadyCastVotes';
import { getVoteEmoji, getVoteLabel } from './voteLabelLookup';
import { voteCountText } from './voteCountText';

interface Props {
  match: Pick<FullMatchFragment, 'id' | 'team' | 'voteCount'>;
}

const confirmProps = {
  title: 'Release votes',
  children: (
    <p>
      This will end the voting for this match and allow all the cast votes to be
      viewed. This can't be undone. Are you sure?
    </p>
  ),
};

export const VotingInProgress: React.FC<Props> = ({ match }) => {
  const { alreadyCastVotes, fetching: votesCastLoading } = useAlreadyCastVotes(
    match.id,
  );
  const [closeVotingResponse, closeMatchVoting] = useCloseMatchVotingMutation();
  const confirm = useConfirm();
  const alert = useAlert();

  if (votesCastLoading) {
    return <LoadingSpinner />;
  }

  const availableVoteTypes = match.team.voteTypes.filter((vt) => vt.enabled);

  const allVotesCast = availableVoteTypes.every((avt) =>
    alreadyCastVotes.includes(avt.id),
  );

  const voteTypeCount = match.team.voteTypes.filter((vt) => vt.enabled).length;

  const submitCloseMatchVoting = async () => {
    const result = await confirm(confirmProps);

    if (result) {
      closeMatchVoting({
        id: match.id,
      }).then((res) => {
        pipe(
          buildMutationMessage('Votes released!')(
            res.data?.closeMatchVoting,
            res.error,
          ),
          E.fold((error) => alert(error, 'danger'), alert),
        );
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2 bg-grey-100 rounded-md p-4 px-6 text-sm">
        <p className="font-semibold">Voting in progress.</p>
        <p>{voteCountText(match.voteCount, voteTypeCount)}</p>
      </div>

      <div
        className={`grid gap-4 ${
          availableVoteTypes.length > 1 ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {availableVoteTypes.map((voteType) =>
          alreadyCastVotes.includes(voteType.id) ? (
            <div
              className="text-center px-4 py-6 text-grey-500 bg-grey-100 rounded-md"
              key={voteType.id}
            >
              <div className="text-3xl pb-3 opacity-50">
                {getVoteEmoji(voteType.name)}
              </div>

              <p>Vote for {getVoteLabel(voteType.name)} has been cast</p>
            </div>
          ) : (
            <InternalLink
              className="text-center px-4 py-6 rounded-md border-grey-100 hover:border-primary-500 border-2"
              key={voteType.id}
              to={paths.match.votes.create(match.id, voteType.id)}
            >
              <div className="text-3xl pb-3">{getVoteEmoji(voteType.name)}</div>

              <p>
                Vote for <strong>{getVoteLabel(voteType.name)}</strong>
              </p>
            </InternalLink>
          ),
        )}
      </div>

      {match.team.isCaptain ? (
        <SubmitButton
          onClick={submitCloseMatchVoting}
          loading={closeVotingResponse.fetching}
          icon="rss"
        >
          Release votes
        </SubmitButton>
      ) : allVotesCast ? (
        <p className="text-center">
          You've cast all your votes. Hang tight until the votes get released.
        </p>
      ) : null}

      <ButtonLink to={paths.match.home(match.id)} kind="plain">
        Back to match
      </ButtonLink>
    </div>
  );
};
